'use client';

import { cn } from '@/lib/cn';
import { createContext, useContext, type ReactNode } from 'react';

const AgentHomeNavContext = createContext(false);

// eslint-disable-next-line react-refresh/only-export-components
export const AGENT_HOME_NAV_ROW = 'h-9 gap-2.5 px-3 hover:bg-surface-accent-hover';

/**
 * Resting / hover text colour for rail rows that open a thread (Pinned,
 * Recents, Scheduled) — quieter than the primary destinations above them.
 * Keyed to the whole `SidebarMenuItem` so hovering the row's overflow action
 * or opening its menu lifts the label too. Selected rows reach
 * `text-text-primary` through SidebarMenuButton's `data-[active=true]` rule.
 */
// eslint-disable-next-line react-refresh/only-export-components
export const AGENT_HOME_THREAD_ROW_TEXT = cn(
  'text-text-secondary group-hover/menu-item:text-text-primary',
  'group-has-[[data-sidebar=menu-action][data-state=open]]/menu-item:text-text-primary'
);

/**
 * One hover state for a thread row with a trailing overflow action: the row
 * button highlights while anywhere in its `SidebarMenuItem` is hovered —
 * including the absolutely positioned action, a sibling the button's own
 * `:hover` never sees — and stays highlighted while that action's menu is open.
 * The selected row keeps its pressed background under either state.
 */
// eslint-disable-next-line react-refresh/only-export-components
export const AGENT_HOME_THREAD_ROW_HOVER = cn(
  'group-hover/menu-item:bg-surface-accent-hover',
  'group-has-[[data-sidebar=menu-action][data-state=open]]/menu-item:bg-background-secondary-hover',
  'data-[active=true]:group-hover/menu-item:bg-background-secondary-pressed',
  'data-[active=true]:group-has-[[data-sidebar=menu-action][data-state=open]]/menu-item:bg-background-secondary-pressed'
);

/** The row's overflow action carries no hover background of its own; its focus ring stays. */
// eslint-disable-next-line react-refresh/only-export-components
export const AGENT_HOME_ROW_ACTION = 'hover:bg-transparent';

/**
 * Marks the subtree rendering the Agent Home navigation so its denser visual
 * treatment does not leak into the existing primary sidebar.
 */
export const AgentHomeNavProvider = ({ children }: { children: ReactNode }) => (
  <AgentHomeNavContext.Provider value={true}>{children}</AgentHomeNavContext.Provider>
);

// This colocated hook is the context's only consumer API; there is no component
// state for Fast Refresh to preserve in this module.
// eslint-disable-next-line react-refresh/only-export-components
export const useIsAgentHomeNav = (): boolean => useContext(AgentHomeNavContext);
